import React, {
  useEffect,
  useState,
  useContext,
  useCallback,
  useRef,
} from "react";
import api from "../services/api";
import { AuthContext } from "../context/AuthContext";
import { SurveyContext } from "../context/SurveyContext";
import { useNewsfeed } from "../context/NewsfeedContext";
import Message from "./Message";
import PostMessage from "./PostMessage";
import PinnedArticleCard from "./PinnedArticleCard";
import WhoToFollow from "./WhoToFollow";
import TrendingHashtags from "./TrendingHashtags";
import { FaArrowUp, FaGlobe, FaUserFriends } from "react-icons/fa";
import FloatingComposeButton from "./FloatingComposeButton";
import EngagementBanner from "./EngagementBanner";

const MESSAGES_PER_PAGE = 10;
const SCROLL_NUDGE_THRESHOLD = 5; // Posts scrolled past before showing nudge

const Newsfeed = () => {
  const { user } = useContext(AuthContext);
  const { condition, instanceId } = useContext(SurveyContext);
  const { markNewContentAvailable, clearNewContent, refreshTrigger } = useNewsfeed();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [interactionCount, setInteractionCount] = useState(0);
  const [newContentAvailable, setNewContentAvailable] = useState(false);
  const [activeTab, setActiveTab] = useState("foryou");
  const [showScrollNudge, setShowScrollNudge] = useState(false);
  const scrollNudgeShownRef = useRef(false);
  const hasPostedRef = useRef(!!sessionStorage.getItem("spiders_has_posted"));
  const observer = useRef();

  // Signal that participant has entered the platform (for Navbar timer)
  // and initialize agent activity timestamp for silence detection
  useEffect(() => {
    window.__studyPlatformEntered = true;
    window.__lastAgentActivityTime = Date.now();
  }, []);

  // ── Scroll depth tracking ──
  const scrollRef = useRef({ maxPercent: 0, totalScrollPx: 0, lastScrollY: 0 });

  useEffect(() => {
    const handleScroll = () => {
      const sr = scrollRef.current;
      const scrollY = window.scrollY;
      const docHeight = document.documentElement.scrollHeight;
      const winHeight = window.innerHeight;
      const percent = docHeight > winHeight
        ? Math.min(100, Math.round(((scrollY + winHeight) / docHeight) * 100))
        : 100;

      sr.totalScrollPx += Math.abs(scrollY - sr.lastScrollY);
      sr.lastScrollY = scrollY;
      if (percent > sr.maxPercent) sr.maxPercent = percent;
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // ── Dwell time tracking ──
  const dwellRef = useRef({}); // { messageId: { dwellTimeMs, enterCount, lastEnterTime } }

  const handleVisibilityChange = useCallback((messageId, isVisible) => {
    const acc = dwellRef.current;
    if (isVisible) {
      if (!acc[messageId]) {
        acc[messageId] = { dwellTimeMs: 0, enterCount: 0, lastEnterTime: null };
      }
      acc[messageId].enterCount += 1;
      acc[messageId].lastEnterTime = Date.now();
    } else if (acc[messageId]?.lastEnterTime) {
      acc[messageId].dwellTimeMs += Date.now() - acc[messageId].lastEnterTime;
      acc[messageId].lastEnterTime = null;
    }
  }, []);

  const getScrollSnapshot = useCallback(() => {
    const sr = scrollRef.current;
    const docHeight = document.documentElement.scrollHeight;
    const winHeight = window.innerHeight;
    const currentPercent = docHeight > winHeight
      ? Math.min(100, Math.round(((window.scrollY + winHeight) / docHeight) * 100))
      : 100;
    return {
      maxPercent: sr.maxPercent,
      currentPercent,
      totalScrollPx: Math.round(sr.totalScrollPx),
    };
  }, []);

  const flushImpressions = useCallback((useKeepalive = false) => {
    const acc = dwellRef.current;
    const now = Date.now();

    // Finalize any still-visible messages
    for (const entry of Object.values(acc)) {
      if (entry.lastEnterTime) {
        entry.dwellTimeMs += now - entry.lastEnterTime;
        entry.lastEnterTime = useKeepalive ? null : now; // reset for continued tracking unless unloading
      }
    }

    const impressions = Object.entries(acc)
      .filter(([, v]) => v.dwellTimeMs > 0)
      .map(([messageId, v]) => ({
        messageId,
        dwellTimeMs: v.dwellTimeMs,
        enterCount: v.enterCount,
      }));

    if (impressions.length === 0) return;

    const scrollDepth = getScrollSnapshot();

    if (useKeepalive) {
      // Page unload — use fetch with keepalive for reliability
      const apiUrl = (process.env.REACT_APP_API_URL || "http://localhost:5001/api");
      const token = sessionStorage.getItem("token");
      try {
        fetch(`${apiUrl}/messages/impressions/batch`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ impressions, condition, instanceId, scrollDepth }),
          keepalive: true,
        });
      } catch (e) {
        // Best-effort on unload
      }
    } else {
      api.post("/messages/impressions/batch", { impressions, condition, instanceId, scrollDepth }).catch(() => {});
    }

    // Reset accumulator
    dwellRef.current = {};
  }, [condition, instanceId, getScrollSnapshot]);

  // Flush every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => flushImpressions(false), 30000);
    return () => {
      clearInterval(interval);
      flushImpressions(false); // Flush on unmount
    };
  }, [flushImpressions]);

  // Flush on page unload
  useEffect(() => {
    const handleUnload = () => flushImpressions(true);
    window.addEventListener("beforeunload", handleUnload);
    return () => window.removeEventListener("beforeunload", handleUnload);
  }, [flushImpressions]);

  const lastMessageElementRef = useCallback(
    (node) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setPage((prevPage) => prevPage + 1);
        }
      });
      if (node) observer.current.observe(node);
    },
    [loading, hasMore]
  );

  const fetchInteractionCount = async () => {
    if (!user || !user.userId) return;
    try {
      const response = await api.get(`/interactions/${user.userId}/count`, {
        params: { condition },
      });
      setInteractionCount(response.data.count);
    } catch (error) {
      console.error("Error fetching interaction count:", error);
    }
  };

  useEffect(() => {
    fetchInteractionCount();
  }, [user, condition]);

  const fetchMessages = async (page, forceUpdate = false, feedType = activeTab) => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/messages`, {
        params: {
          page,
          limit: MESSAGES_PER_PAGE,
          condition,
          instanceId,
          feed: feedType,
        },
      });

      if (forceUpdate || page === 1) {
        setMessages(response.data);
      } else {
        setMessages((prevMessages) => {
          const existingIds = new Set(prevMessages.map(m => m._id));
          const newMsgs = response.data.filter(m => !existingIds.has(m._id));
          return [...prevMessages, ...newMsgs];
        });
      }

      setHasMore(response.data.length === MESSAGES_PER_PAGE);
      setNewContentAvailable(false);
      if (forceUpdate || page === 1) {
        clearNewContent();
      }
    } catch (error) {
      console.error("Error fetching messages:", error);
      setError("Failed to fetch messages. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages(1, true, activeTab);
  }, [condition, activeTab]);

  useEffect(() => {
    if (refreshTrigger > 0) {
      setPage(1);
      fetchMessages(1, true);
    }
  }, [refreshTrigger]);

  useEffect(() => {
    if (page > 1) {
      fetchMessages(page);
    }
  }, [page]);

  // Poll for new content every 30 seconds
  useEffect(() => {
    if (!user || !condition) return;

    const pollForNewContent = async () => {
      try {
        const response = await api.get(`/messages`, {
          params: {
            page: 1,
            limit: 1,
            condition,
            instanceId,
          },
        });

        if (response.data.length > 0 && messages.length > 0) {
          const latestMessage = response.data[0];
          const currentLatest = messages[0];

          if (new Date(latestMessage.timestamp) > new Date(currentLatest.timestamp)) {
            markNewContentAvailable();
            setNewContentAvailable(true);
            // Track agent activity for silence detection in Navbar
            window.__lastAgentActivityTime = Date.now();
          }
        }
      } catch (error) {
        console.error("Error polling for new content:", error);
      }
    };

    const interval = setInterval(pollForNewContent, 30000);
    return () => clearInterval(interval);
  }, [user, condition, messages, markNewContentAvailable]);

  // Scroll nudge: after scrolling past N posts AND spending at least 60 seconds, show a prompt
  const feedStartTimeRef = useRef(Date.now());
  useEffect(() => {
    if (scrollNudgeShownRef.current || hasPostedRef.current) return;

    const handleScroll = () => {
      if (scrollNudgeShownRef.current) return;
      const scrolledPosts = Math.floor(window.scrollY / 200);
      const elapsedSeconds = (Date.now() - feedStartTimeRef.current) / 1000;
      if (scrolledPosts >= SCROLL_NUDGE_THRESHOLD && elapsedSeconds >= 60) {
        setShowScrollNudge(true);
        scrollNudgeShownRef.current = true;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleInteraction = useCallback(
    async (type, targetId, messageId, newLikeCount) => {
      if (!user || !user.userId) {
        console.error("User or user ID is not available");
        return;
      }
      try {
        // Only call recordInteraction for types NOT already logged server-side.
        // Likes/unlikes are logged by POST /messages/:id/like,
        // comments are logged by POST /comments. Only expand_comments
        // and other frontend-only types need the explicit call.
        const serverLoggedTypes = ["like", "unlike", "comment"];
        if (!serverLoggedTypes.includes(type)) {
          await api.recordInteraction(user.userId, type, targetId);
        }
        setInteractionCount((prevCount) => prevCount + 1);
        markNewContentAvailable();

        if (type === "like" || type === "unlike") {
          setMessages((prevMessages) =>
            prevMessages.map((message) => {
              if (message._id === messageId) {
                return {
                  ...message,
                  likes:
                    type === "like"
                      ? [...message.likes, user.userId]
                      : message.likes.filter((id) => id !== user.userId),
                  likeCount: newLikeCount,
                };
              }
              return message;
            })
          );
        }
      } catch (error) {
        console.error("Error recording interaction:", error);
      }
    },
    [user, setInteractionCount, markNewContentAvailable, setMessages]
  );

  const handleUpdateNewsfeed = () => {
    flushImpressions(false); // Flush dwell data before refreshing feed
    setPage(1);
    fetchMessages(1, true);
    clearNewContent();
    setNewContentAvailable(false);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  return (
    <div className="min-h-screen bg-spiders-bg">
      <div className="flex justify-center gap-6 px-0 lg:px-4 py-6">
        {/* Main Feed Column */}
        <main className="w-full max-w-full lg:max-w-[600px] space-y-4">
          {/* Post Composer Card */}
          <div className="bg-spiders-bg-surface rounded-2xl overflow-hidden border border-spiders-accent-400 border-opacity-30">
            <PostMessage
              onNewPost={(newMessage) => {
                setMessages((prevMessages) => [
                  {
                    ...newMessage,
                    author: newMessage.author || {
                      _id: user.userId,
                      username: user.username,
                      avatarSeed: user.avatarSeed,
                    },
                  },
                  ...prevMessages,
                ]);
                markNewContentAvailable();
                hasPostedRef.current = true;
                setShowScrollNudge(false);
              }}
            />
          </div>

          {/* Pinned Topic Article */}
          {window.__studyTopicArticle && (
            <PinnedArticleCard article={window.__studyTopicArticle} />
          )}

          {/* Feed Toggle */}
          <div className="flex border-b border-spiders-border">
            <button
              onClick={() => { setActiveTab("foryou"); setPage(1); }}
              className={`flex-1 py-2 text-sm font-medium flex items-center justify-center gap-1.5 transition-colors relative ${
                activeTab === "foryou"
                  ? "text-spiders-text-primary"
                  : "text-spiders-text-muted hover:text-spiders-text-secondary"
              }`}
            >
              <FaGlobe className="w-3 h-3" />
              For You
              {activeTab === "foryou" && (
                <span className="absolute bottom-0 left-1/4 right-1/4 h-0.5 bg-spiders-primary-400 rounded-full" />
              )}
            </button>
            <button
              onClick={() => { setActiveTab("following"); setPage(1); }}
              className={`flex-1 py-2 text-sm font-medium flex items-center justify-center gap-1.5 transition-colors relative ${
                activeTab === "following"
                  ? "text-spiders-text-primary"
                  : "text-spiders-text-muted hover:text-spiders-text-secondary"
              }`}
            >
              <FaUserFriends className="w-3 h-3" />
              Following
              {activeTab === "following" && (
                <span className="absolute bottom-0 left-1/4 right-1/4 h-0.5 bg-spiders-primary-400 rounded-full" />
              )}
            </button>
          </div>

          {/* Error State */}
          {error && (
            <div className="p-4 text-spiders-error text-center bg-spiders-bg-surface rounded-2xl border border-spiders-error/30">
              {error}
            </div>
          )}

          {/* New Posts Button — inline between composer and posts, sticky so it stays visible while scrolling */}
          {newContentAvailable && (
            <div className="sticky top-1 z-30 flex justify-center py-2">
              <button
                onClick={handleUpdateNewsfeed}
                className="bg-spiders-primary-500 hover:bg-spiders-primary-600 text-white px-5 py-2 rounded-full shadow-lg shadow-spiders-primary-500/25 flex items-center space-x-2 transition-all hover:scale-105 text-sm"
              >
                <FaArrowUp className="w-3.5 h-3.5" />
                <span className="font-medium">New posts</span>
              </button>
            </div>
          )}

          {/* Posts Container */}
          <div className="bg-spiders-bg-surface rounded-2xl overflow-hidden">
            {messages.map((message, index) => (
              <div
                key={message._id}
                ref={index === messages.length - 1 ? lastMessageElementRef : null}
              >
                <Message message={message} onInteraction={handleInteraction} onVisibilityChange={handleVisibilityChange} feedMode={true} />
              </div>
            ))}

            {/* Loading State */}
            {loading && (
              <div className="flex items-center justify-center py-8">
                <div className="w-6 h-6 border-2 border-spiders-primary-400 border-t-transparent rounded-full animate-spin" />
              </div>
            )}

            {/* End of Feed */}
            {!hasMore && messages.length > 0 && (
              <div className="py-12 text-center">
                <p className="text-spiders-text-muted mb-2">You're all caught up!</p>
                <p className="text-spiders-text-muted text-sm">Check back later for new posts</p>
              </div>
            )}

            {/* Empty State */}
            {!loading && messages.length === 0 && (
              <div className="py-16 text-center">
                <p className="text-spiders-text-muted text-lg mb-2">No posts yet</p>
                <p className="text-spiders-text-muted text-sm">Be the first to share something!</p>
              </div>
            )}
          </div>
        </main>

        {/* Right Sidebar */}
        <aside className="hidden lg:block w-[350px] sticky top-6 max-h-[calc(100vh-3rem)] overflow-y-auto">
          <div className="space-y-4">
            <WhoToFollow onInteraction={handleInteraction} />
            <TrendingHashtags />
          </div>
        </aside>
      </div>

      <FloatingComposeButton />

      {/* Engagement banner: shows when user's posts get responses */}
      <EngagementBanner
        userPostIds={messages
          .filter((m) => m.author?._id === user?.userId || m.author?.username === user?.username)
          .map((m) => m._id)}
        condition={condition}
      />

      {/* Scroll nudge - shown once per session when user scrolls without posting */}
      {showScrollNudge && (
        <div className="fixed bottom-20 lg:bottom-8 left-1/2 -translate-x-1/2 z-40 bg-spiders-bg-elevated border border-spiders-border rounded-xl shadow-lg px-5 py-3 flex items-center space-x-3 animate-fadeIn">
          <span className="text-sm text-spiders-text-secondary">
            You've been reading — want to share your take?
          </span>
          <button
            onClick={() => {
              setShowScrollNudge(false);
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            className="spiders-btn-primary text-xs py-1.5 px-3 whitespace-nowrap"
          >
            Write something
          </button>
          <button
            onClick={() => setShowScrollNudge(false)}
            className="text-spiders-text-muted hover:text-spiders-text-primary text-xs"
          >
            ✕
          </button>
        </div>
      )}

    </div>
  );
};

export default Newsfeed;
